/**
 * Shared pieces for showing a document job (R1 P3′): its status icon, its
 * factual line, and the links to what it produced — each written section,
 * the suggestion list, the question list. Used by the Active AI tasks tray
 * and the AI panel's document-job card. Wording lives in
 * `@/lib/document-job-progress` (placeholder copy).
 */

import { AlertCircle, CheckCircle2, ChevronRight, Clock, Loader2, X } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/utils'
import {
  documentJobLine,
  documentJobLinks,
  documentJobProgressPercent,
  documentJobState,
  type DocumentJobLike,
  type ProgressSectionResult,
  type SectionLink,
} from '@/lib/document-job-progress'

// ===================
// Copy (placeholder)
// ===================

const RESULT_WORD: Record<ProgressSectionResult, string> = {
  saved: 'Written',
  suggested: 'Suggestion',
  drafted: 'Drafted',
  skipped: 'Skipped',
  failed: "Couldn't write",
  not_drafted: 'Not drafted',
}

function reviewLabel(count: number): string {
  return `Review ${count} ${count === 1 ? 'suggestion' : 'suggestions'}`
}

function answerLabel(count: number): string {
  return `Answer ${count} ${count === 1 ? 'question' : 'questions'}`
}

/** The overview recap's per-section pill: this section has a suggestion waiting for review. */
const SUGGESTION_READY = 'Suggestion ready'
/** The overview recap's link to a section. */
const OPEN_LABEL = 'Open'

/** Results that leave nothing to open, said beside the section in the overview recap. */
const UNWRITTEN_RESULTS: ReadonlySet<ProgressSectionResult> = new Set(['skipped', 'failed', 'not_drafted'])

// ===================
// Pieces
// ===================

export function DocumentJobStatusIcon({ job }: { job: DocumentJobLike }) {
  switch (documentJobState(job)) {
    case 'waiting':
      return <Clock className="h-4 w-4 text-muted-foreground" aria-label="Waiting" />
    case 'running':
      return <Loader2 className="h-4 w-4 animate-spin text-[hsl(var(--ai-accent))]" aria-label="Running" />
    case 'saved':
      return <CheckCircle2 className="h-4 w-4 text-[hsl(var(--status-success))]" aria-label="Saved" />
    case 'partial':
      return <AlertCircle className="h-4 w-4 text-[hsl(var(--status-warning))]" aria-label="Partly done" />
    case 'failed':
      return <AlertCircle className="h-4 w-4 text-destructive" aria-label="Failed" />
    case 'cancelled':
      return <X className="h-4 w-4 text-muted-foreground" aria-label="Cancelled" />
  }
}

interface LinkButtonProps {
  label: string
  onClick: () => void
}

function LinkButton({ label, onClick }: LinkButtonProps) {
  return (
    <button
      type="button"
      className="inline-flex items-center gap-0.5 text-xs text-[hsl(var(--link))] hover:underline"
      onClick={onClick}
    >
      {label}
      <ChevronRight className="h-3.5 w-3.5" />
    </button>
  )
}

interface DocumentJobLinksRowProps {
  job: DocumentJobLike
  onNavigate: (href: string) => void
  className?: string
}

/** "Review N suggestions" → the document's suggestion list; "Answer N questions" → the document overview's question list. */
export function DocumentJobLinksRow({ job, onNavigate, className }: DocumentJobLinksRowProps) {
  const { suggestions, questions } = documentJobLinks(job)
  if (!suggestions && !questions) return null
  return (
    <div className={cn('flex flex-wrap items-center gap-3', className)}>
      {suggestions && <LinkButton label={reviewLabel(suggestions.count)} onClick={() => onNavigate(suggestions.href)} />}
      {questions && <LinkButton label={answerLabel(questions.count)} onClick={() => onNavigate(questions.href)} />}
    </div>
  )
}

interface DocumentJobSectionListProps {
  job: DocumentJobLike
  onNavigate: (href: string) => void
}

/** Every section the job settled, each written one linking to itself. */
export function DocumentJobSectionList({ job, onNavigate }: DocumentJobSectionListProps) {
  const { sections } = documentJobLinks(job)
  if (sections.length === 0) return null
  return (
    <ul className="divide-y divide-border" data-testid="document-job-sections">
      {sections.map((section) => (
        <li key={section.sectionKey} className="flex items-center justify-between gap-2 py-1.5 text-sm">
          <span className="min-w-0 flex-1">
            <span className="block truncate">{section.heading}</span>
            <span className="block text-meta truncate">
              {RESULT_WORD[section.result]}
              {section.reason ? ` · ${section.reason}` : ''}
            </span>
          </span>
          {section.href && (
            <LinkButton label="Open" onClick={() => onNavigate(section.href!)} />
          )}
        </li>
      ))}
    </ul>
  )
}

interface DocumentJobRecapSectionListProps {
  sections: ReadonlyArray<SectionLink>
  /** Whether a section has a suggestion waiting for review. */
  isPending: (section: SectionLink) => boolean
  onNavigate: (href: string) => void
}

/**
 * The overview recap's sections (draft page redesign): each name, a
 * "Suggestion ready" pill while its suggestion waits, why nothing was written
 * where nothing was, and "Open" where there is something to open.
 */
export function DocumentJobRecapSectionList({ sections, isPending, onNavigate }: DocumentJobRecapSectionListProps) {
  if (sections.length === 0) return null
  return (
    <ul className="divide-y divide-border rounded-lg border border-border bg-card" data-testid="document-job-sections">
      {sections.map((section) => (
        <li key={section.sectionKey} className="flex items-center gap-2.5 px-3 py-2.5 text-sm" data-testid="document-job-section">
          <span className="min-w-0 flex-1 truncate font-medium">{section.heading}</span>
          {isPending(section) ? (
            <Badge variant="review" className="hidden shrink-0 sm:inline-flex">{SUGGESTION_READY}</Badge>
          ) : (
            UNWRITTEN_RESULTS.has(section.result) && (
              <span className="min-w-0 max-w-[50%] truncate text-meta">
                {RESULT_WORD[section.result]}
                {section.reason ? ` · ${section.reason}` : ''}
              </span>
            )
          )}
          {section.href && <LinkButton label={OPEN_LABEL} onClick={() => onNavigate(section.href!)} />}
        </li>
      ))}
    </ul>
  )
}

interface DocumentJobTrayDetailProps {
  job: DocumentJobLike
  onNavigate: (href: string) => void
}

/** The tray row's body for a document job: its factual line, a bar while it writes, and its links. */
export function DocumentJobTrayDetail({ job, onNavigate }: DocumentJobTrayDetailProps) {
  const state = documentJobState(job)
  const percent = documentJobProgressPercent(job.document)
  return (
    <>
      <p
        className={cn(
          'text-[11px] mt-0.5',
          state === 'failed' ? 'text-destructive' : 'text-muted-foreground'
        )}
      >
        {documentJobLine(job)}
      </p>
      {state === 'running' && percent > 0 && (
        <div className="mt-1.5 h-1 w-full overflow-hidden rounded-full bg-muted">
          <div
            className="h-full rounded-full bg-[hsl(var(--ai-accent))] transition-all"
            style={{ width: `${percent}%` }}
          />
        </div>
      )}
      <DocumentJobLinksRow job={job} onNavigate={onNavigate} className="mt-1" />
    </>
  )
}
